/* eslint-disable no-undef, no-underscore-dangle */
import { getField, updateField } from 'vuex-map-fields';
import moment from 'moment-timezone';
import { getClients, addClient } from '@/services/business';
import {
  getAvailableAppointments,
  addAppointment,
  updateAppointment,
} from '@/services/appointment';
import {
  SET_CLIENT,
  SET_CURRENT_SEARCHED_CLIENTS,
  SET_CURRENT_DAY,
  SET_CURRENT_APPOINTMENT,
  SET_APPOINTMENT_TYPE,
  SET_APPOINTMENT_DETAILS,
  SET_AVAILABLE_APPOINTMENTS,
  SELECT_SLOT,
  SET_OVERLAPPING_SLOTS,
  SET_EDIT_MODE,
  SET_NOTIFICATION_ID,
  CLEAR_BOOKING,
  SHOW_BOOKING_MODAL,
  LOADING,
  LOADING_SUCCESS,
} from './mutation-types';

moment.defaultFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

const getStartAndEndDate = (date, currentTimezone) => {
  const timezoneDate = moment.tz(moment(date).format('YYYY-MM-DDTHH:mm:ss'), 'YYYY-MM-DDTHH:mm:ss', currentTimezone);
  return {
    start: timezoneDate
      .startOf('day')
      .add(9, 'h')
      .format(),
    end: timezoneDate
      .startOf('day')
      .add(19, 'h')
      .format(),
  };
};

const getAppDuration = (typeId, appointmentTypes) => {
  const type = appointmentTypes.find(app => app._id === typeId);
  let length = type.duration / 60;
  if (!length) {
    length = 60;
  }
  return length;
};

function removeDuplicatesBy(keyFn, array) {
  const mySet = new Set();
  return array.filter((x) => {
    const key = keyFn(x);
    const isNew = !mySet.has(key);
    if (isNew) mySet.add(key);
    return isNew;
  });
}

const getUniqueAppTimes = (appArray) => {
  const uniqueArray = removeDuplicatesBy(x => x.start_time._i, appArray);
  // const uniqueArray = appArray.filter((obj, pos, arr) => {
  //   const mappedArr = arr.map(mapObj => mapObj.start_time);
  //   console.log('is it unique?', obj.start_time, pos);
  //   return mappedArr.indexOf(obj.start_time) === pos;
  // });
  uniqueArray.sort((a, b) => {
    if (a.start_time.isBefore(b.start_time)) {
      return -1;
    }
    return 1;
  });
  return uniqueArray;
};


const formatDoctorApps = (aptsByDoctors) => {
  let mappedArray = [];
  aptsByDoctors.forEach((app) => {
    app.mappedApps = app.time_intervals.map(int => ({
      ...int,
      start_time: moment.parseZone(int.start_time),
      end_time: moment.parseZone(int.end_time),
      doctor_id: app.doctor_id,
    }));
    mappedArray = mappedArray.concat(app.mappedApps);
  });
  return getUniqueAppTimes(mappedArray);
};

const mapClientForCall = client => ({
  first_name: client.firstName,
  last_name: client.lastName,
  phone_number: client.phoneNumber,
  email: client.email,
});

const mapAppointment = (state, { locationId, userId }) => {
  const app = {
    location_id: locationId,
    associate_user_id: userId,
    start_time: state.selectedSlot.start_time,
    end_time: state.selectedSlot.end_time,
    doctor_id: state.selectedSlot.doctor_id,
    description: state.appointment.description,
    notes: state.appointment.notes,
    type: state.appointment.type,
    _id: state.appointment._id,
  };

  app.client_id = state.client._id || state.client.id;
  return app;
};

const getOverlappingSlots = (day, duration, { currentTimezone, doctorId }) => {
  const slots = [];
  let startHour = moment.tz(day, currentTimezone).startOf('day')
    .add(9, 'h');
  const endHour = moment.tz(day, currentTimezone).startOf('day')
    .add(19, 'h');
  while (moment(startHour).add(duration, 'm').isSameOrBefore(endHour)) {
    slots.push({
      start_time: startHour,
      end_time: moment(startHour).add(duration, 'm'),
      doctor_id: doctorId,
    });
    startHour = moment(startHour).add(15, 'm');
  }
  return slots;
};

const getClient = state => new Promise((resolve, reject) => {
  if (!state.client._id) {
    return addClient(mapClientForCall(state.client))
      .then((res) => {
        const { data } = res;
        return resolve(data);
      })
      .catch(err => reject(err));
  }
  return resolve(state.client);
});


const initialState = {
  editMode: false,
  client: {},
  newClient: {},
  availableSlots: [],
  overlappingSlots: [],
  selectedSlot: null,
  currentSearchedClients: [],
  currentDay: null,
  showOverlaps: false,
  notificationId: null,
  appointment: {
    notes: '',
    type: '',
    description: null,
    start_time: null,
    end_time: null,
  },
};

const booking = {
  state: initialState,
  mutations: {
    [SET_CLIENT](state, client) {
      state.client = client;
    },
    [SET_CURRENT_SEARCHED_CLIENTS](state, clients) {
      state.currentSearchedClients = clients;
    },
    [SET_CURRENT_DAY](state, day) {
      state.currentDay = day;
    },
    [SET_AVAILABLE_APPOINTMENTS](state, slots) {
      state.availableSlots = slots;
    },
    [SET_OVERLAPPING_SLOTS](state, { rootGetters, duration }) {
      const { businessHours, currentTimezone, doctorId } = rootGetters;
      state.overlappingSlots = getOverlappingSlots(
        state.currentDay,
        duration,
        { businessHours, currentTimezone, doctorId },
      );
    },
    [SELECT_SLOT](state, slot) {
      state.selectedSlot = slot;
    },
    [SET_EDIT_MODE](state, editMode) {
      state.editMode = editMode;
    },
    [SET_NOTIFICATION_ID](state, notificationId) {
      state.notificationId = notificationId;
    },
    [SET_CURRENT_APPOINTMENT](state, appointment) {
      state.appointment = appointment;
    },
    [SET_APPOINTMENT_TYPE](state, type) {
      state.appointment.type = type;
    },
    [SET_APPOINTMENT_DETAILS](state, { type, notes, description }) {
      state.appointment.type = type;
      state.appointment.notes = notes;
      state.appointment.description = description;
    },
    [CLEAR_BOOKING](state) {
      state.editMode = false;
      state.client = {};
      state.selectedSlot = null;
      state.notificationId = null;
      state.currentSearchedClients = [];
      state.showOverlaps = false;
      state.appointment = {
        notes: '',
        type: '',
        description: null,
        start_time: null,
        end_time: null,
      };
    },
    updateField,
  },
  actions: {
    setCurrentDay({ commit }, day) {
      commit(SET_CURRENT_DAY, day);
    },
    bookNotification({ commit, dispatch }, appointment) {
      commit(SET_NOTIFICATION_ID, appointment._id);
      dispatch('openEditMode', appointment);
    },
    bookSelectedDate({ commit, rootState, rootGetters }) {
      const currentDate = rootState.appointment.currentCalendarDay;
      const duration = getAppDuration(
        rootState.appointmentTypes.list[0]._id,
        rootState.appointmentTypes.list,
      );
      const startTime = moment.tz(currentDate, rootGetters.currentTimezone);
      const slot = {
        start_time: startTime,
        end_time: moment(startTime).add(duration, 'm'),
        doctor_id: rootGetters.doctorId,
      };
      commit(SELECT_SLOT, slot);
      commit(SHOW_BOOKING_MODAL);
    },
    openEditMode({ commit, rootGetters }, currentAppointment) {
      commit(LOADING);
      const slot = {
        start_time: moment.tz(currentAppointment.start_time, rootGetters.currentTimezone),
        end_time: moment.tz(currentAppointment.end_time, rootGetters.currentTimezone),
        doctor_id: currentAppointment.doctor_id,
      };
      commit(SELECT_SLOT, slot);
      commit(SET_CLIENT, currentAppointment.client);
      commit(SET_CURRENT_DAY, moment.tz(
        currentAppointment.start_time,
        rootGetters.currentTimezone,
      ));
      commit(SET_CURRENT_APPOINTMENT, currentAppointment);
      commit(SET_EDIT_MODE, true);
      commit(SHOW_BOOKING_MODAL);
      commit(LOADING_SUCCESS);
    },
    clearBooking({ commit }) {
      commit(CLEAR_BOOKING);
    },
    selectSlot({ commit }, slot) {
      commit(SELECT_SLOT, slot);
    },
    setClient({ commit }, client) {
      commit(SET_CLIENT, client);
    },
    setAppointmentType({ commit }, type) {
      commit(SET_APPOINTMENT_TYPE, type);
    },
    setAppointmentDetails({ commit }, form) {
      commit(SET_APPOINTMENT_DETAILS, form);
    },
    setOverlappingSlots({ commit, rootGetters, rootState }, typeId) {
      const duration = getAppDuration(typeId, rootState.appointmentTypes.list);
      commit(SET_OVERLAPPING_SLOTS, { rootGetters, duration });
    },
    setFormData({ commit }, form) {
      commit(SET_CLIENT, form);
      commit(SET_APPOINTMENT_DETAILS, form);
    },
    getClients({ commit, state }, string) {
      return new Promise((resolve, reject) => {
        if (string.length === 0) {
          commit(SET_CURRENT_SEARCHED_CLIENTS, []);
          return resolve(state.currentSearchedClients);
        }
        return getClients(string)
          .then((res) => {
            const { data } = res;
            if (!data || !data.clients) {
              reject(new Error('Could not find client.'));
            } else {
              commit(SET_CURRENT_SEARCHED_CLIENTS, data.clients);
              resolve(state.currentSearchedClients);
            }
          })
          .catch(err => reject(err));
      });
    },
    getAvailableAppointments({
      commit, state, rootGetters, rootState,
    }, typeId) {
      const { currentTimezone, locationId } = rootGetters;
      const callArgs = [
        getStartAndEndDate(state.currentDay, currentTimezone),
        locationId,
        getAppDuration(typeId, rootState.appointmentTypes.list),
      ];
      if (state.editMode) {
        callArgs.push(state.appointment._id);
      }
      commit(SET_AVAILABLE_APPOINTMENTS, []);
      return new Promise((resolve, reject) =>
        getAvailableAppointments(...callArgs)
          .then((res) => {
            const { data } = res;
            if (!data || !data.available_times) {
              reject(new Error('Could not find available appointment slot.'));
            } else {
              commit(SET_AVAILABLE_APPOINTMENTS, formatDoctorApps(data.available_times));
              resolve(state.availableSlots);
            }
          })
          .catch(err => reject(err)));
    },
    bookAppointment({
      commit, state, rootGetters, dispatch,
    }) {
      commit(LOADING);
      const { locationId, userId } = rootGetters;
      return new Promise((resolve, reject) =>
        getClient(state)
          .then((client) => {
            commit(SET_CLIENT, client);
            const appointment = mapAppointment(state, { locationId, userId });
            return addAppointment(appointment);
          })
          .then((res) => {
            const { data } = res;
            return dispatch('addAppointment', data.Appointment);
          })
          .then((newApp) => {
            if (state.notificationId !== null) {
              return dispatch('hideNotification', state.notificationId)
                .then(() => {
                  commit(SET_NOTIFICATION_ID, null);
                  commit(LOADING_SUCCESS);
                  resolve(newApp);
                });
            }
            commit(LOADING_SUCCESS);
            return resolve(newApp);
          })
          .catch((err) => {
            commit(LOADING_SUCCESS);
            if (!err) {
              return reject(new Error('Could not connect to server.'));
            }
            return reject(err);
          }));
    },
    updateAppointment({
      commit, state, rootGetters, dispatch,
    }) {
      commit(LOADING);
      const { locationId, userId } = rootGetters;
      const appointment = mapAppointment(state, { locationId, userId });
      return dispatch('updateAppointmentCall', appointment);
    },
    rescheduleAppointment({
      commit, dispatch,
    }, appointment) {
      commit(LOADING);
      return dispatch('updateAppointmentCall', appointment);
    },
    updateAppointmentCall({
      commit, dispatch,
    }, appointment) {
      return new Promise((resolve, reject) =>
        updateAppointment(appointment)
          .then(() => dispatch('changeAppointment', appointment))
          .then((updatedApp) => {
            commit(LOADING_SUCCESS);
            resolve(updatedApp);
          })
          .catch((err) => {
            commit(LOADING_SUCCESS);
            if (!err) {
              return reject(new Error('Could not connect to server.'));
            }
            return reject(err);
          }));
    },
    setDefaultAppointmentType({ commit, rootState }) {
      commit(SET_APPOINTMENT_TYPE, rootState.appointmentTypes.list[0]);
    },
    updateSelectedSlot({ commit, state }) {
      if (!state.selectedSlot) {
        return;
      }
      const targetArray = state.showOverlaps ? state.overlappingSlots : state.availableSlots;
      const newSlot = targetArray.find(ts => ts.start_time.isSame(state.selectedSlot.start_time));
      if (newSlot) {
        commit(SELECT_SLOT, newSlot);
      } else {
        commit(SELECT_SLOT, null);
      }
    },
  },
  getters: {
    getField,
    notificationMode: state => state.notificationId !== null,
    enableClientForm: state => !state.client._id,
  },
};

export default booking;



// WEBPACK FOOTER //
// ./src/store/booking.js